/**
 * @file Price-prompt modal — collects a CZK amount with formula-suggested
 * default and a soft warning when the entered value diverges from the
 * suggestion. Used by the offboarding workflow when finalising a
 * "Mark bought" or "Send to loot" decision so the admin can override
 * the formula's resale value before Slack/CSV fire.
 *
 * Returns a Promise that resolves to:
 *   • a positive Number — admin confirmed with a valid price
 *   • null              — admin cancelled (no price; caller should abort)
 *
 * Resolves only on explicit Confirm or Cancel — clicking outside the
 * backdrop does NOT close the modal (avoids accidental cancels during
 * the offboarding flow when the rest of the page is dense with
 * clickable rows).
 */

import { el } from '../utils.js';

/**
 * @param opts.title       modal heading text
 * @param opts.subtitle    short context line ("Mark bought for X laptop")
 * @param opts.label       input label ("Sale price" / "Loot price")
 * @param opts.suggested   formula-suggested number (0 disables the diff hint)
 * @param opts.priceNote   extra fine-print under the input (e.g. VAT note)
 * @param opts.confirmLabel button text
 */
export function promptForPrice(opts) {
  const {
    title = 'Enter price',
    subtitle = '',
    label = 'Price',
    suggested = 0,
    priceNote = '',
    confirmLabel = 'Confirm',
  } = opts;

  return new Promise((resolve) => {
    const backdrop = el('div', { class: 'modal-backdrop open' });
    const input = el('input', {
      type: 'number',
      min: '0',
      step: '1',
      value: suggested > 0 ? String(suggested) : '',
      placeholder: 'CZK',
      style: 'width:160px',
    });
    const diffNote = el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:4px 0 0;min-height:1.2em' }, '');

    function refresh() {
      const v = Number(input.value);
      if (!Number.isFinite(v) || v <= 0) {
        diffNote.textContent = 'Enter a price greater than 0.';
        diffNote.style.color = 'var(--color-error)';
        return;
      }
      if (!suggested || suggested <= 0) {
        diffNote.textContent = '';
        diffNote.style.color = '';
        return;
      }
      const diff = v - suggested;
      if (Math.abs(diff) < 1) {
        diffNote.textContent = `Matches the formula suggestion (${suggested} CZK).`;
        diffNote.style.color = '';
      } else if (diff < 0) {
        diffNote.textContent = `Below the formula suggestion of ${suggested} CZK by ${Math.abs(diff)} CZK. OK to proceed — admin discretion.`;
        diffNote.style.color = 'var(--color-error)';
      } else {
        diffNote.textContent = `Above the formula suggestion of ${suggested} CZK by ${diff} CZK.`;
        diffNote.style.color = '';
      }
    }
    input.addEventListener('input', refresh);
    refresh();

    const close = (result) => {
      backdrop.remove();
      document.removeEventListener('keydown', onKey);
      resolve(result);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close(null);
      else if (e.key === 'Enter') confirm();
    };
    const confirm = () => {
      const v = Number(input.value);
      if (!Number.isFinite(v) || v <= 0) {
        input.focus();
        return;
      }
      close(v);
    };

    backdrop.appendChild(el('div', { class: 'modal' },
      el('h2', null, title),
      subtitle ? el('p', { class: 'muted', style: 'margin-top:0' }, subtitle) : null,
      el('div', { class: 'field', style: 'margin-top:16px' },
        el('label', null, label),
        el('div', { class: 'row', style: 'gap:6px;align-items:center' },
          input,
          el('span', { class: 'muted' }, 'CZK'),
        ),
        diffNote,
        priceNote ? el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin-top:8px' }, priceNote) : null,
      ),
      el('div', { class: 'row', style: 'margin-top:24px;justify-content:flex-end;gap:8px' },
        el('button', { class: 'btn btn-ghost', onClick: () => close(null) }, 'Cancel'),
        el('button', { class: 'btn btn-primary',           onClick: confirm }, confirmLabel),
      ),
    ));
    document.body.appendChild(backdrop);
    document.addEventListener('keydown', onKey);
    input.focus();
    input.select();
  });
}
